import { create } from 'zustand';
import type { Action } from '@/types/workflowDefinition';

/**
 * Clipboard state for copy/paste operations
 */
interface ClipboardState {
  /** Serialized action data (if any) */
  clipboardData: Action | null;
  /** Name of the action in the clipboard */
  clipboardActionName: string | null;
  /** Current workflow name (to detect workflow switches) */
  currentWorkflowName: string | null;
  
  /** Copy an action to the clipboard */
  copyAction: (action: Action, actionName: string) => void;
  /** Clear the clipboard */
  clearClipboard: () => void;
  /** Set current workflow (auto-clears clipboard on switch) */
  setCurrentWorkflow: (workflowName: string | null) => void;
}

/**
 * Clipboard store for copy/paste operations.
 * Automatically clears when switching workflows.
 */
export const useClipboardStore = create<ClipboardState>((set, get) => ({
  clipboardData: null,
  clipboardActionName: null,
  currentWorkflowName: null,

  copyAction: (action: Action, actionName: string) => {
    // Deep clone to avoid reference issues
    const clonedAction = JSON.parse(JSON.stringify(action)) as Action;
    set({ 
      clipboardData: clonedAction,
      clipboardActionName: actionName,
    });
  },

  clearClipboard: () => {
    set({ 
      clipboardData: null,
      clipboardActionName: null,
    });
  },

  setCurrentWorkflow: (workflowName: string | null) => {
    const { currentWorkflowName } = get();
    // Clear clipboard when switching to a different workflow (including null↔non-null)
    if (currentWorkflowName !== workflowName) {
      set({ 
        clipboardData: null,
        clipboardActionName: null,
      });
    }
    set({ currentWorkflowName: workflowName });
  },
}));
